// SlimeDuel — Background Game Announcer (Voiceover + Procedural Stingers).
// Handles key match moments without cluttering the mix:
//   - "IT'S... TIME!" Bruce Buffer–style opener when a match first loads in
//   - 3, 5, 10 splat streaks (local player)
//   - 30 seconds remaining callout
//   - Final 10-second voice countdown (10..1)
//   - Water / sea fall death (long, drawn-out "Wommmp... wommmmp" sad trombone + voiceover)
// Voice: deep male arena / futuristic duel-FPS announcer (low pitch, deliberate pace, layered with synth stingers).
// Controlled via Settings → Audio → Announcer (0..1, persisted in settings.announcer).

import { G, on, clamp } from '../core/ctx.js';
import { DEFAULT_SETTINGS } from '../config.js';

// Announcer delivery baseline: deep + deliberate. Each callout scales off these.
const BASE_PITCH = 0.62;
const BASE_RATE = 0.96;

const COUNT_WORDS = {
  10: 'Ten!', 9: 'Nine!', 8: 'Eight!', 7: 'Seven!', 6: 'Six!',
  5: 'Five!', 4: 'Four!', 3: 'Three!', 2: 'Two!', 1: 'One!',
};

const STREAK_LINES = {
  3: { parts: [{ text: 'Triple splat!', pitch: 0.66, rate: 1.0 }, { text: 'Dominating!', pitch: 0.6, rate: 0.9 }], sfxPitch: 1.0 },
  5: { parts: [{ text: 'Five splat streak!', pitch: 0.66, rate: 1.0 }, { text: 'Unstoppable!', pitch: 0.58, rate: 0.85 }], sfxPitch: 1.15 },
  10: { parts: [{ text: 'Ten splat streak!', pitch: 0.66, rate: 0.98 }, { text: 'Legendary!', pitch: 0.55, rate: 0.72 }], sfxPitch: 1.32 },
};

// Male voices across Windows (SAPI + Edge neural), macOS and Chrome. Female voices are excluded outright.
const MALE_VOICES = [
  /Microsoft (Guy|Christopher|Eric|Andrew|Brian|Roger|Steffan|Davis|Tony|Jason|Ryan|Thomas|William)\b/i,
  /Microsoft (David|Mark|George|James|Richard|Sean)\b/i,
  /Google UK English Male/i,
  /\b(Daniel|Alex|Fred|Tom|Aaron|Arthur|Oliver|Rishi|Gordon|Lee|Reed|Rocko|Eddy|Grandpa)\b/i,
  /\bMale\b/i,
];
const FEMALE_VOICES = /\b(Zira|Hazel|Susan|Linda|Heera|Catherine|Aria|Jenny|Michelle|Ana|Emma|Ava|Libby|Sonia|Natasha|Clara|Jane|Nancy|Sara|Samantha|Victoria|Karen|Moira|Tessa|Fiona|Serena|Allison|Kathy|Vicki|Princess|Female|Google US English|Google UK English Female)\b/i;

export class Announcer {
  constructor() {
    this.streak = 0;
    this.voice = null;
    this._voicesLoaded = false;
    this._unlocked = false;
    this._lastSpeakAt = 0;
    this._lastPreviewAt = 0;
    this._countingDown = false;
    this._openedMatch = null;   // the match we've already said "It's time!" for
    this._speakTimer = 0;
    this._utterances = new Set(); // Prevent V8 GC from collecting active utterances mid-speech
    this._initVoices();
    this._installUnlock();
    this._bindEvents();
  }

  _initVoices() {
    if (typeof window === 'undefined' || !('speechSynthesis' in window)) return;
    const pickVoice = () => {
      let voices = [];
      try { voices = window.speechSynthesis.getVoices() || []; } catch (e) { voices = []; }
      if (!voices.length) return;
      this._voicesLoaded = true;
      const en = voices.filter((v) => /^en(-|_|$)/i.test(v.lang));
      const pool = en.length ? en : voices;
      // 1) local male voice (most reliable in Chromium), 2) any male voice (e.g. Edge neural "Guy"), 3) any non-female local voice
      const find = (list) => { for (const rx of MALE_VOICES) { const hit = list.find((v) => rx.test(v.name) && !FEMALE_VOICES.test(v.name)); if (hit) return hit; } return null; };
      this.voice = find(pool.filter((v) => v.localService)) || find(pool)
        || pool.find((v) => v.localService && !FEMALE_VOICES.test(v.name))
        || pool.find((v) => !FEMALE_VOICES.test(v.name))
        || pool[0] || null;
    };
    pickVoice();
    if (window.speechSynthesis.onvoiceschanged !== undefined) {
      window.speechSynthesis.addEventListener?.('voiceschanged', pickVoice);
    }
  }

  _installUnlock() {
    if (typeof window === 'undefined' || !('speechSynthesis' in window)) return;
    const unlock = () => {
      if (this._unlocked) return;
      this._unlocked = true;
      try {
        this._initVoices();
        if (window.speechSynthesis.paused) window.speechSynthesis.resume();
        // Prime Chromium's speech synthesis engine on first user gesture with a silent micro-utterance
        const u = new SpeechSynthesisUtterance('');
        u.volume = 0;
        window.speechSynthesis.speak(u);
      } catch (e) { /* ignore */ }
    };
    for (const ev of ['pointerdown', 'keydown', 'mousedown', 'touchend']) {
      window.addEventListener(ev, unlock, { capture: true, passive: true, once: true });
    }
  }

  // Effective announcer volume (0..1) combining Master * Announcer setting
  _volume() {
    const s = G.settings || DEFAULT_SETTINGS;
    const master = clamp(s.master ?? DEFAULT_SETTINGS.master ?? 0.8, 0, 1);
    const ann = clamp(s.announcer ?? DEFAULT_SETTINGS.announcer ?? 0.85, 0, 1);
    return master * ann;
  }

  stop() {
    if (this._speakTimer) {
      clearTimeout(this._speakTimer);
      this._speakTimer = 0;
    }
    this._utterances.clear();
    if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
      try { window.speechSynthesis.cancel(); } catch (e) { /* ignore */ }
    }
  }

  // text: a string, or an array of { text, pitch, rate } segments spoken back-to-back (lets one callout stretch / shift
  // delivery mid-line, e.g. "It's..." → "TIME!").
  speak(text, { pitch = BASE_PITCH, rate = BASE_RATE, volume = 1.0, interrupt = true } = {}) {
    const eff = this._volume() * volume;
    if (eff <= 0.005) return;
    if (typeof window === 'undefined' || !('speechSynthesis' in window) || typeof SpeechSynthesisUtterance === 'undefined') return;
    const parts = Array.isArray(text) ? text : [{ text, pitch, rate }];
    try {
      if (!this._voicesLoaded || !this.voice) this._initVoices();
      const synth = window.speechSynthesis;
      if (synth.paused) synth.resume();

      if (this._speakTimer) {
        clearTimeout(this._speakTimer);
        this._speakTimer = 0;
      }

      const enqueue = () => {
        try {
          if (synth.paused) synth.resume();
          for (const seg of parts) {
            const u = new SpeechSynthesisUtterance(seg.text);
            if (this.voice) u.voice = this.voice;
            u.lang = this.voice?.lang || 'en-US';
            u.pitch = clamp(seg.pitch ?? pitch, 0.1, 1.8);
            u.rate = clamp(seg.rate ?? rate, 0.3, 1.7);
            u.volume = clamp(eff * (seg.volume ?? 1), 0, 1);
            this._utterances.add(u);
            const cleanup = () => this._utterances.delete(u);
            u.onend = cleanup;
            u.onerror = cleanup;
            synth.speak(u);
          }
          this._lastSpeakAt = performance.now();
        } catch (e) { /* ignore */ }
      };

      // In Chromium on Windows, calling synth.cancel() and synth.speak() on the same synchronous tick
      // can cancel the newly queued utterance too. Only cancel if actively speaking, and wait 25ms before speaking.
      if (interrupt && (synth.speaking || synth.pending)) {
        synth.cancel();
        this._utterances.clear();
        this._speakTimer = setTimeout(() => {
          this._speakTimer = 0;
          enqueue();
        }, 25);
      } else {
        enqueue();
      }
    } catch (e) {
      /* speech synthesis blocked or unavailable */
    }
  }

  // Audible test when adjusting the Announcer slider in Settings → Audio
  preview() {
    const now = performance.now();
    if (now - this._lastPreviewAt < 550) return;
    this._lastPreviewAt = now;
    if (this._volume() <= 0.005) {
      this.stop();
      return;
    }
    G.audio?.init?.();
    G.audio?.play?.('announcer_streak', { volume: 0.9, pitch: 1.05 });
    this.speak([{ text: 'Triple splat!', pitch: 0.66, rate: 1.0 }, { text: 'Dominating!', pitch: 0.6, rate: 0.9 }], { interrupt: true });
  }

  // "IT'SSSS... TIIIIIME!" — slow, stretched, the second word lands lower and longer, over a synth riser + impact.
  _itsTime() {
    if (this._volume() <= 0.005) return;
    G.audio?.init?.();
    G.audio?.duck?.(0.4, 3.4);
    G.audio?.play?.('announcer_intro', { volume: 1.0 });
    clearTimeout(this._introTimer);
    this._introTimer = setTimeout(() => {
      this.speak([
        { text: "It's", pitch: 0.7, rate: 0.42 },
        { text: 'Tiiime!', pitch: 0.5, rate: 0.34 },
      ], { interrupt: true });
    }, 250);
  }

  _isLiveMatch() {
    const m = G.match;
    return !!(m && !m.attract && G.mode === 'match' && m.state === 'playing' && !m.paused);
  }

  _bindEvents() {
    // Reset state on match transitions
    on('match:state', ({ state, match }) => {
      if (!match || match.attract) return;
      if (state === 'intro' || state === 'playing') {
        // First time this match comes alive (intro cam, or straight to playing in practice): the opener
        if (this._openedMatch !== match) {
          this._openedMatch = match;
          this.streak = 0;
          this._itsTime();
        }
        this._countingDown = false;
      } else if (state === 'finish' || state === 'judge' || state === 'results') {
        this._countingDown = false;
        clearTimeout(this._introTimer);
        this.stop();
      }
    });

    // 30 seconds remaining callout
    on('match:thirtysec', () => {
      if (!this._isLiveMatch() || this._volume() <= 0.005) return;
      G.audio?.init?.();
      G.audio?.duck?.(0.55, 1.8);
      G.audio?.play?.('announcer_30s', { volume: 0.95 });
      this.speak([{ text: 'Thirty seconds', pitch: 0.62, rate: 0.92 }, { text: 'remaining!', pitch: 0.56, rate: 0.86 }], { interrupt: true });
    });

    // Final 10 seconds countdown (10..1)
    on('match:count', ({ n }) => {
      if (!this._isLiveMatch() || this._volume() <= 0.005) return;
      const word = COUNT_WORDS[n];
      if (!word) return;
      this._countingDown = true;
      G.audio?.init?.();
      // Slightly higher urgency as the clock approaches 1 (still in the deep register)
      const urgency = clamp((10 - n) / 9, 0, 1);
      G.audio?.play?.('announcer_count', { volume: 0.75 + 0.25 * urgency, pitch: 1 + urgency * 0.2 });
      this.speak(word, {
        pitch: 0.6 + urgency * 0.12,
        rate: 1.15,
        interrupt: true,
      });
    });

    // Splat streaks (3, 5, 10) & water fall ("womp womp")
    on('splatted', ({ victim, attacker, cause }) => {
      if (!this._isLiveMatch() || !victim) return;
      const local = G.match?.local;
      if (!local) return;

      // Local player was splatted
      if (victim === local || victim.isLocal) {
        this.streak = 0;
        const isWater = cause === 'water' || cause === 'sea' || cause === 'fall';
        if (isWater && this._volume() > 0.005) {
          G.audio?.init?.();
          G.audio?.duck?.(0.35, 4.2);
          // Long, drawn-out sad trombone + a slow, sinking "Wommmp... wommmmmp" right after the splash impact
          setTimeout(() => {
            if (this._volume() <= 0.005) return;
            G.audio?.play?.('announcer_womp', { volume: 1.0 });
            if (!this._countingDown) {
              this.speak([
                { text: 'Wommmp...', pitch: 0.5, rate: 0.4 },
                { text: 'wommmmmp.', pitch: 0.3, rate: 0.3 },
              ], { volume: 1.0, interrupt: true });
            }
          }, 120);
        }
        return;
      }

      // Local player scored a splat
      if (attacker && (attacker === local || attacker.isLocal)) {
        this.streak++;
        const cfg = STREAK_LINES[this.streak];
        if (cfg && this._volume() > 0.005) {
          G.audio?.init?.();
          G.audio?.duck?.(0.5, 2.4);
          G.audio?.play?.('announcer_streak', { volume: 1.0, pitch: cfg.sfxPitch });
          // Don't interrupt the final 10-second voice countdown if it's actively ticking
          if (!this._countingDown) {
            this.speak(cfg.parts, { interrupt: true });
          }
        }
      }
    });
  }
}
